import {
  BanknotesIcon,
  PencilIcon,
  PlusCircleIcon,
  ScaleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import {
  ChangesSummary,
  CollapsibleDetails,
  CreatedEntityPattern,
  DeletionPattern,
  EventBlock,
  EventEmphasis,
  EventRow,
  EventText,
  EventUser,
  UserActionPattern,
} from '@/components/domain-events-v2/shared';
import { RegistryDefinitions } from '@/components/domain-events-v2/types';

// Helper to format currency
const formatCurrency = (amount: string | number): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

// Helper to format date
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const ledgerEvents: RegistryDefinitions = {
  // ============================================================================
  // EXPENSE EVENTS
  // ============================================================================
  expense_created: {
    icon: PlusCircleIcon,
    iconColor: 'text-emerald-500',
    priority: 'high',
    render: (event) => {
      const { payload } = event;
      const { expense } = payload;

      const details = expense.description ? (
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-500">Service date:</span>
            <span className="font-medium text-gray-900">
              {formatDate(expense.service_date)}
            </span>
          </div>
          {expense.description && (
            <div className="flex justify-between">
              <span className="text-gray-500">Description:</span>
              <span className="text-gray-700">{expense.description}</span>
            </div>
          )}
        </div>
      ) : undefined;

      return (
        <CreatedEntityPattern
          event={event}
          entityType="expense"
          entityName={
            <EventBlock>
              <EventRow>
                <EventEmphasis>
                  {formatCurrency(expense.billed_amount)}
                </EventEmphasis>
                <EventText>from</EventText>
                <EventEmphasis>{expense.provider.name}</EventEmphasis>
              </EventRow>
            </EventBlock>
          }
          tag={{ text: 'Medical Bill', color: 'emerald' }}
          details={details}
        />
      );
    },
    description: (event) => {
      const { expense } = event.payload;
      return `created expense: ${expense.provider.name} - ${formatCurrency(expense.billed_amount)}`;
    },
  },

  expense_updated: {
    icon: PencilIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event) => {
      const { payload } = event;
      const { expense } = payload;

      return (
        <UserActionPattern
          event={event}
          action="updated expense"
          details={
            payload.diff ? (
              <ChangesSummary diff={payload.diff as Record<string, unknown>} />
            ) : undefined
          }
        >
          <EventEmphasis>{formatCurrency(expense.billed_amount)}</EventEmphasis>
          <EventText>from</EventText>
          <EventEmphasis>{expense.provider.name}</EventEmphasis>
        </UserActionPattern>
      );
    },
    description: (event) => {
      const { expense } = event.payload;
      return `updated expense from ${expense.provider.name}`;
    },
  },

  expense_deleted: {
    icon: TrashIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event) => {
      const { payload } = event;
      const { expense } = payload;

      return (
        <DeletionPattern
          event={event}
          entityType="expense"
          entityName={`${expense.provider.name} - ${formatCurrency(expense.billed_amount)}`}
        />
      );
    },
    description: (event) => {
      const { expense } = event.payload;
      return `deleted expense from ${expense.provider.name}`;
    },
  },

  allocations_replaced: {
    icon: BanknotesIcon,
    iconColor: 'text-blue-500',
    priority: 'medium',
    render: (event) => {
      const { payload } = event;
      const { expense } = payload;
      const prevAllocations = payload.previous_allocations || [];
      const newAllocations = payload.new_allocations || [];

      const formatAllocation = (alloc: {
        payer_type?: string | null;
        amount: string;
      }) => {
        const payerType = alloc.payer_type?.replace(/_/g, ' ') || 'Unknown';
        return `${payerType}: ${formatCurrency(alloc.amount)}`;
      };

      const details = (
        <div className="space-y-2 text-xs">
          {prevAllocations.length > 0 && (
            <div>
              <div className="text-gray-500 font-medium mb-1">Previous:</div>
              <div className="space-y-0.5">
                {prevAllocations.map((alloc, idx) => (
                  <div
                    key={idx}
                    className="text-red-600 bg-red-50 px-1.5 py-0.5 rounded line-through decoration-red-300/50"
                  >
                    {formatAllocation(alloc)}
                  </div>
                ))}
              </div>
            </div>
          )}
          {newAllocations.length > 0 && (
            <div>
              <div className="text-gray-500 font-medium mb-1">New:</div>
              <div className="space-y-0.5">
                {newAllocations.map((alloc, idx) => (
                  <div
                    key={idx}
                    className="text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded font-medium"
                  >
                    {formatAllocation(alloc)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );

      const content = (
        <EventRow>
          <EventUser event={event} />
          <EventText>updated payment allocation for</EventText>
          <EventEmphasis>{formatCurrency(expense.billed_amount)}</EventEmphasis>
          <EventText>from</EventText>
          <EventEmphasis>{expense.provider.name}</EventEmphasis>
        </EventRow>
      );

      return (
        <CollapsibleDetails summary={content}>{details}</CollapsibleDetails>
      );
    },
    description: (event) => {
      const { expense } = event.payload;
      return `updated payment allocation for ${expense.provider.name}`;
    },
  },

  // ============================================================================
  // LIEN EVENTS
  // ============================================================================
  lien_created: {
    icon: ScaleIcon,
    iconColor: 'text-amber-500',
    priority: 'high',
    render: (event) => {
      const { payload } = event;
      const { lien } = payload;

      const details = (
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-500">Type:</span>
            <span className="font-medium text-gray-900">
              {lien.lienholder_type.replace(/_/g, ' ')}
            </span>
          </div>
          {lien.exposure_amount && (
            <div className="flex justify-between">
              <span className="text-gray-500">Amount:</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(lien.exposure_amount)}
              </span>
            </div>
          )}
        </div>
      );

      return (
        <CreatedEntityPattern
          event={event}
          entityType="lien"
          entityName={
            <EventBlock>
              <EventRow>
                {lien.lienholder_provider ? (
                  <EventEmphasis>{lien.lienholder_provider.name}</EventEmphasis>
                ) : (
                  <EventEmphasis>
                    {lien.lienholder_coverage?.carrier_name ||
                      lien.lienholder_type.replace(/_/g, ' ')}
                  </EventEmphasis>
                )}
              </EventRow>
            </EventBlock>
          }
          tag={{ text: 'Lien', color: 'amber' }}
          details={details}
        />
      );
    },
    description: (event) => {
      const { lien } = event.payload;
      const name =
        lien.lienholder_provider?.name ||
        lien.lienholder_coverage?.carrier_name ||
        lien.lienholder_type;
      return `created lien: ${name}`;
    },
  },

  lien_updated: {
    icon: PencilIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event) => {
      const { payload } = event;
      const { lien } = payload;

      const lienName =
        lien.lienholder_provider?.name ||
        lien.lienholder_coverage?.carrier_name ||
        lien.lienholder_type.replace(/_/g, ' ');

      return (
        <UserActionPattern
          event={event}
          action="updated lien"
          details={
            payload.diff ? (
              <ChangesSummary diff={payload.diff as Record<string, unknown>} />
            ) : undefined
          }
        >
          <EventEmphasis>{lienName}</EventEmphasis>
        </UserActionPattern>
      );
    },
    description: (event) => {
      const { lien } = event.payload;
      const name =
        lien.lienholder_provider?.name ||
        lien.lienholder_coverage?.carrier_name;
      return `updated lien: ${name || lien.lienholder_type}`;
    },
  },

  lien_deleted: {
    icon: TrashIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event) => {
      const { payload } = event;
      const { lien } = payload;

      const lienName =
        lien.lienholder_provider?.name ||
        lien.lienholder_coverage?.carrier_name ||
        lien.lienholder_type.replace(/_/g, ' ');

      return (
        <DeletionPattern
          event={event}
          entityType="lien"
          entityName={lienName}
        />
      );
    },
    description: (event) => {
      const { lien } = event.payload;
      const name =
        lien.lienholder_provider?.name ||
        lien.lienholder_coverage?.carrier_name;
      return `deleted lien: ${name || lien.lienholder_type.replace(/_/g, ' ')}`;
    },
  },
};
